import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, throwError } from 'rxjs';

import { API_PAGE_SIZE } from '../../core/constants';
import { ApiError } from '../api/api-error';
import { OrdersApi } from '../api/orders.api';
import { buildOrderPayload, mapOrder, mapOrderPage } from '../mappers/order.mapper';
import type { Order, OrderDraft, OrdersFilter } from '../models/order/order.model';
import type { Paged } from '../models/paged.model';
import type { ApiEnvelope } from './api-envelope';
import { apiPath, mapApiError, unwrapEnvelope } from './api-envelope';

const ORDER_PATH = '/api/users/client/order';

const SERVER_ORDERS_FILTER: Record<OrdersFilter, string> = { active: 'ACTIVE', history: 'HISTORY' };

@Injectable()
export class OrdersHttpApi extends OrdersApi {
  private readonly http = inject(HttpClient);

  override getOrders(page: number, filter: OrdersFilter): Observable<Paged<Order>> {
    const params = new HttpParams()
      .set('page', page)
      .set('size', API_PAGE_SIZE)
      .set('filter', SERVER_ORDERS_FILTER[filter]);
    return this.http
      .get<ApiEnvelope<unknown> | null>(apiPath(ORDER_PATH), { params })
      .pipe(unwrapEnvelope<unknown>(), map(mapOrderPage), mapApiError());
  }

  override getOrder(id: string): Observable<Order | null> {
    return this.http
      .get<ApiEnvelope<unknown> | null>(apiPath(`${ORDER_PATH}/${encodeURIComponent(id)}`))
      .pipe(unwrapEnvelope<unknown>(), map(mapOrder), mapApiError());
  }

  override placeOrder(draft: OrderDraft): Observable<Order> {
    const payload = buildOrderPayload(draft);
    if (payload === null) {
      // Невалидный черновик — ошибка вызывающего: такой заказ отвергнет и сервер, сеть не трогаем.
      return throwError(() => new ApiError(400, 'unknown'));
    }

    return this.http
      .post<ApiEnvelope<unknown> | null>(apiPath(ORDER_PATH), payload)
      .pipe(unwrapEnvelope<unknown>(), map(readOrder), mapApiError());
  }
}

/** Заказ уже создан: нечитаемый ответ — сбой сервера, а не «заказа нет», повторять POST нельзя. */
function readOrder(raw: unknown): Order {
  const order = mapOrder(raw);
  if (order === null) {
    throw new ApiError(500, 'unknown');
  }
  return order;
}
